import { useState } from "react";
import { useAtomValue } from "jotai";
import { apiCall } from "../../../lib/api-client.js";
import { ApiError, type PublicRole, type RosterSyncResult } from "../../../lib/api-types.js";
import { authAtom } from "../../../lib/auth-store.js";
import { Drawer } from "../../../components/Drawer.js";
import { ErrorBanner } from "../../../components/ErrorBanner.js";
import { InlineLoading } from "../../../components/Spinner.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
} from "../../../components/FormField.js";
import { usersPath, feishuSyncPath } from "../../../lib/api-paths.js";
import { FeishuDeptTree } from "../FeishuDeptTree.js";

/**
 * Fix 7 · MVP 飞书部门批量导入。
 *
 * UX：admin 填 dept openDepartmentId → 点拉取 → 后端 live 调飞书 API 取成员列表
 * + 标 alreadyImported；admin 勾选 + 配批量 role / enableDosia / defaultPassword
 * → 提交 POST /users/bulk-import-from-feishu → 显示 created / skipped / failed
 * 摘要。
 *
 * MVP 限制（独立 follow-up PR 完善）：
 *   - 用户名默认 = 后端拼音登录名（member.suggestedUser，绝不用 openId）；admin 可逐条 override
 *   - 邀请方式只支持 system-temp-password（飞书私信发链接需独立调用 IM API，留后续）
 *
 * Phase D 跟进：嵌入 <FeishuDeptTree mode="single" /> 让 admin 直接树上点选部门，
 *   点选后立即拉成员；保留手动粘贴 openDepartmentId 作为折叠 fallback。
 */

interface FeishuMember {
  openId: string;
  unionId?: string;
  name?: string;
  mobile?: string;
  email?: string;
  avatarUrl?: string;
  departmentIds?: string[];
  alreadyImported: boolean;
  importedUser?: { id: string; user: string };
  /** 后端拼音登录名默认值（绝不为 openId）；空则前端 sanitize 兜底。 */
  suggestedUser?: string;
}

interface MembersResponse {
  members: FeishuMember[];
}

interface BulkImportResponse {
  created: Array<{
    openId: string;
    user: string;
    id: string;
    dosiaEmployeeId?: string;
    generatedPassword?: string;
  }>;
  skipped: Array<{ openId: string; user: string; reason: string }>;
  failed: Array<{ openId: string; user: string; error: string }>;
}

function sanitizeForUser(name?: string): string {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function BulkImportDialog({
  orgCode,
  roles,
  onClose,
  onCompleted,
}: {
  orgCode: string;
  roles: PublicRole[];
  onClose: () => void;
  onCompleted: () => void;
}): JSX.Element {
  const auth = useAtomValue(authAtom);
  const [deptId, setDeptId] = useState("");
  const [members, setMembers] = useState<FeishuMember[]>([]);
  const [selectedByOpenId, setSelectedByOpenId] = useState<Map<string, string>>(
    new Map(),
  );
  const [defaultRoleCode, setDefaultRoleCode] = useState<string>("");
  const [defaultEnableDosia, setDefaultEnableDosia] = useState(false);
  /** 密码策略（2026-07-04）：默认 姓名+手机号后四位，一键全员开号后可逐个改 */
  const [passwordMode, setPasswordMode] = useState<
    "name-mobile4" | "custom" | "random"
  >("name-mobile4");
  const [defaultPassword, setDefaultPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkImportResponse | null>(null);
  const [syncingTree, setSyncingTree] = useState(false);
  const [treeRefreshKey, setTreeRefreshKey] = useState(0);

  const handleSyncStructure = async (): Promise<void> => {
    if (!auth) return;
    setSyncingTree(true);
    setError(null);
    try {
      await apiCall<{ ok: true; summary: RosterSyncResult }>(
        "POST",
        feishuSyncPath(orgCode),
        { token: auth.token },
      );
      setTreeRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "同步飞书架构失败");
    } finally {
      setSyncingTree(false);
    }
  };

  /**
   * 抽 deptId 显式入参版本，让树 picker 在 setState 还没落地时也能立即触发拉取。
   * 不传 = 用 input 框里的 deptId（"拉取成员"按钮路径）。
   */
  const handleFetch = async (overrideDeptId?: string): Promise<void> => {
    const target = (overrideDeptId ?? deptId).trim();
    if (!auth || !target) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall<MembersResponse>(
        "GET",
        `/api/organizations/${orgCode}/feishu/departments/${encodeURIComponent(
          target,
        )}/members`,
        { token: auth.token },
      );
      setMembers(res.members);
      // 预选所有未导入
      const init = new Map<string, string>();
      for (const m of res.members) {
        if (!m.alreadyImported) {
          init.set(m.openId, m.suggestedUser || sanitizeForUser(m.name));
        }
      }
      setSelectedByOpenId(init);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "拉取成员失败");
    } finally {
      setLoading(false);
    }
  };

  const handlePickDept = (id: string): void => {
    setDeptId(id);
    void handleFetch(id);
  };

  const toggleSelect = (m: FeishuMember): void => {
    if (m.alreadyImported) return;
    setSelectedByOpenId((curr) => {
      const next = new Map(curr);
      if (next.has(m.openId)) next.delete(m.openId);
      else next.set(m.openId, m.suggestedUser || sanitizeForUser(m.name));
      return next;
    });
  };

  const updateUser = (openId: string, user: string): void => {
    setSelectedByOpenId((curr) => {
      const next = new Map(curr);
      next.set(openId, user);
      return next;
    });
  };

  const handleSubmit = async (): Promise<void> => {
    if (!auth || selectedByOpenId.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const items = members
        .filter((m) => selectedByOpenId.has(m.openId))
        .map((m) => ({
          openId: m.openId,
          user: selectedByOpenId.get(m.openId)!,
          enableDosia: defaultEnableDosia,
          feishu: {
            name: m.name,
            unionId: m.unionId,
            avatarUrl: m.avatarUrl,
            mobile: m.mobile,
            email: m.email,
            departmentIds: m.departmentIds,
          },
        }));
      const res = await apiCall<BulkImportResponse>(
        "POST",
        `${usersPath(orgCode)}/bulk-import-from-feishu`,
        {
          token: auth.token,
          body: {
            items,
            defaultRoleCode: defaultRoleCode || undefined,
            passwordPolicy:
              passwordMode === "name-mobile4" ? "name-mobile4" : undefined,
            defaultPassword:
              passwordMode === "custom" ? defaultPassword || undefined : undefined,
          },
        },
      );
      setResult(res);
      onCompleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "导入失败");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Drawer
        open
        onClose={onClose}
        title="批量导入结果"
        width={720}
        footer={
          <PrimaryButton onClick={onClose}>关闭</PrimaryButton>
        }
      >
        <div className="flex flex-col gap-3 text-sm">
          <p className="text-dosia-text">
            创建 <strong>{result.created.length}</strong> · 跳过{" "}
            <strong>{result.skipped.length}</strong> · 失败{" "}
            <strong>{result.failed.length}</strong>
          </p>

          {result.created.length > 0 ? (
            <details open>
              <summary className="cursor-pointer text-dosia-text">
                创建明细 ({result.created.length})
              </summary>
              <table className="w-full text-xs mt-2">
                <thead className="text-dosia-text-secondary">
                  <tr>
                    <th className="text-left py-1">用户名</th>
                    <th className="text-left py-1">openId</th>
                    <th className="text-left py-1">dosiaEmployeeId</th>
                    <th className="text-left py-1">临时密码</th>
                  </tr>
                </thead>
                <tbody>
                  {result.created.map((c) => (
                    <tr key={c.id} className="border-t border-dosia-border">
                      <td className="py-1 font-mono">{c.user}</td>
                      <td className="py-1 font-mono text-dosia-text-dim">
                        {c.openId}
                      </td>
                      <td className="py-1 font-mono text-dosia-text-dim">
                        {c.dosiaEmployeeId ?? "—"}
                      </td>
                      <td className="py-1 font-mono">
                        {c.generatedPassword ?? "—（统一自定义密码）"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="rounded border border-amber-500/30 bg-amber-500/15 px-3 py-2 text-xs text-amber-500 mt-2">
                临时密码仅显示一次，关闭后无法恢复。请立即转告员工或导出。
              </div>
            </details>
          ) : null}

          {result.skipped.length > 0 ? (
            <details>
              <summary className="cursor-pointer text-dosia-text-secondary">
                跳过 ({result.skipped.length})
              </summary>
              <ul className="text-xs mt-2 space-y-1">
                {result.skipped.map((s) => (
                  <li key={s.openId} className="text-dosia-text-dim">
                    {s.user} ({s.openId}) — {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {result.failed.length > 0 ? (
            <details>
              <summary className="cursor-pointer text-amber-500">
                失败 ({result.failed.length})
              </summary>
              <ul className="text-xs mt-2 space-y-1">
                {result.failed.map((f) => (
                  <li key={f.openId} className="text-amber-500">
                    {f.user} ({f.openId}) — {f.error}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title="从飞书部门批量导入"
      width={720}
      footer={
        <>
          <SecondaryButton onClick={onClose} disabled={submitting}>
            取消
          </SecondaryButton>
          <PrimaryButton
            disabled={selectedByOpenId.size === 0 || submitting}
            onClick={() => void handleSubmit()}
          >
            导入选中 ({selectedByOpenId.size})
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {error ? <ErrorBanner onDismiss={() => setError(null)}>{error}</ErrorBanner> : null}

        <FormField
          label="选择飞书部门"
          hint="点节点 = 立即拉取该部门及子部门所有成员；选顶层部门即可一键为全员开号"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-xs text-dosia-text-dim flex-1">
              部门列表非最新？点右边刷新
            </span>
            <SecondaryButton
              onClick={() => void handleSyncStructure()}
              disabled={syncingTree}
            >
              {syncingTree ? "同步中..." : "刷新架构"}
            </SecondaryButton>
          </div>
          <div className="border border-dosia-border rounded-md p-2 max-h-[280px] overflow-auto bg-dosia-bg-light">
            <FeishuDeptTree
              mode="single"
              hideSyncHeader
              refreshKey={treeRefreshKey}
              selected={deptId ? [deptId] : []}
              onToggle={handlePickDept}
            />
          </div>
        </FormField>

        <details className="text-xs text-dosia-text-dim">
          <summary className="cursor-pointer select-none">
            手动输入 openDepartmentId（高级）
          </summary>
          <div className="mt-2 flex gap-2">
            <TextInput
              value={deptId}
              onChange={(e) => setDeptId(e.target.value)}
              placeholder="od_xxxxxxx"
            />
            <SecondaryButton
              onClick={() => void handleFetch()}
              disabled={loading || !deptId.trim()}
            >
              {loading ? "拉取中..." : "拉取成员"}
            </SecondaryButton>
          </div>
        </details>

        {members.length > 0 ? (
          <>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="默认 role" hint="不指定 = admin 后续指派">
                <Select
                  value={defaultRoleCode}
                  onChange={(e) => setDefaultRoleCode(e.target.value)}
                >
                  <option value="">不指定</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.code}>
                      {r.code} ({r.name})
                    </option>
                  ))}
                </Select>
              </FormField>
              <FormField
                label="初始密码"
                hint={
                  passwordMode === "name-mobile4"
                    ? "如 张三1234；缺姓名/手机号的成员回退随机密码"
                    : passwordMode === "custom"
                      ? "全员统一此密码"
                      : "server 生成 8 字符随机，结果页一次性可见"
                }
              >
                <div className="flex flex-col gap-1.5">
                  <Select
                    value={passwordMode}
                    onChange={(e) =>
                      setPasswordMode(
                        e.target.value as "name-mobile4" | "custom" | "random",
                      )
                    }
                    aria-label="密码策略"
                  >
                    <option value="name-mobile4">姓名+手机号后四位（推荐）</option>
                    <option value="custom">统一自定义</option>
                    <option value="random">随机临时密码</option>
                  </Select>
                  {passwordMode === "custom" ? (
                    <TextInput
                      type="text"
                      value={defaultPassword}
                      onChange={(e) => setDefaultPassword(e.target.value)}
                      placeholder="Welcome1"
                    />
                  ) : null}
                </div>
              </FormField>
              <FormField label="启用 dosia desktop">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={defaultEnableDosia}
                    onChange={(e) => setDefaultEnableDosia(e.target.checked)}
                  />
                  全员启用
                </label>
              </FormField>
            </div>

            <p className="text-xs text-dosia-text-dim">
              共 {members.length} 个成员；已导入 {members.filter((m) => m.alreadyImported).length} 个（灰色不可选）。
            </p>

            <div className="border border-dosia-border rounded-md max-h-[360px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-dosia-bg-lighter sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-8"></th>
                    <th className="text-left p-2">飞书姓名</th>
                    <th className="text-left p-2">用户名（可改）</th>
                    <th className="text-left p-2">openId</th>
                    <th className="text-left p-2">状态</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const selected = selectedByOpenId.has(m.openId);
                    return (
                      <tr
                        key={m.openId}
                        className={
                          m.alreadyImported
                            ? "opacity-50"
                            : "hover:bg-dosia-bg-lighter/60"
                        }
                      >
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={m.alreadyImported}
                            onChange={() => toggleSelect(m)}
                            aria-label={`选择 ${m.name ?? m.openId}`}
                          />
                        </td>
                        <td className="p-2 text-dosia-text">{m.name ?? "—"}</td>
                        <td className="p-2">
                          {m.alreadyImported ? (
                            <span className="font-mono text-dosia-text-dim">
                              {m.importedUser?.user ?? "—"}
                            </span>
                          ) : selected ? (
                            <input
                              type="text"
                              value={selectedByOpenId.get(m.openId) ?? ""}
                              onChange={(e) => updateUser(m.openId, e.target.value)}
                              className="w-full font-mono rounded border border-dosia-border bg-dosia-bg-input px-1 py-0.5"
                            />
                          ) : (
                            <span className="text-dosia-text-dim">未选</span>
                          )}
                        </td>
                        <td className="p-2 font-mono text-dosia-text-dim">
                          {m.openId}
                        </td>
                        <td className="p-2">
                          {m.alreadyImported ? (
                            <span className="text-emerald-400">已导入✓</span>
                          ) : (
                            <span className="text-dosia-text-dim">未导入</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : !loading ? (
          <p className="text-xs text-dosia-text-dim">
            输入部门 openDepartmentId 然后点「拉取成员」开始。
          </p>
        ) : (
          <InlineLoading />
        )}
      </div>
    </Drawer>
  );
}
